import * as analytics from 'analytics/Analytics';
import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {
  NdcGermlineConfig,
  NdcSpreadsheetComponentFormatType,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import NestedCreateAndRegisterAntibodiesSpreadsheetImportModal from 'antibodies/nested-data-creation/spreadsheet-import/NestedCreateAndRegisterAntibodiesSpreadsheetImportModal';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';

export interface ProteinCreationSettingsData {
  selectedFormat: AntibodyFormat;
  selectedSchemaId: string;
  selectedSchemaName: string;
  selectedRegistryId: string;
  selectedStartingType: NdcSpreadsheetComponentFormatType;
  germlineConfig: NdcGermlineConfig;
}

const SETTINGS_KEY = 'protein-creation-settings';

const ProteinCreationSettingsGlobalState = makeUseGlobalClientState<ProteinCreationSettingsData | null>(null);

export function setProteinCreationSettings(data: ProteinCreationSettingsData): void {
  ProteinCreationSettingsGlobalState.updateState(SETTINGS_KEY, data);
}

export function getProteinCreationSettings(): ProteinCreationSettingsData | null {
  return ProteinCreationSettingsGlobalState.getState(SETTINGS_KEY);
}

export function clearProteinCreationSettings(): void {
  ProteinCreationSettingsGlobalState.deleteState(SETTINGS_KEY);
}

export function useProteinCreationSettings(): [
  ProteinCreationSettingsData | null,
  (data: ProteinCreationSettingsData | null) => void,
] {
  return ProteinCreationSettingsGlobalState.useGlobalClientState(SETTINGS_KEY);
}

/**
 * Opens the protein creation settings modal if no settings are already set.
 * If the user completes step 1, the modal writes settings.
 * Returns whether the user submitted the protein creation settings.
 */
export async function handleOpenProteinCreationFlow(): Promise<boolean> {
  if (getProteinCreationSettings() != null) {
    return true;
  }
  LoadingScreen.init();
  await legacyRunModal(NestedCreateAndRegisterAntibodiesSpreadsheetImportModal, {
    analyticsMetadata: {
      actionOrigin: ActionOrigin.ANTIBODY_NESTED_DATA_CREATION,
      eventGroupId: analytics.makeAnalyticsEventId(),
    },
  });
  return getProteinCreationSettings() != null;
}
